// src/components/mobile/event/components/ChatView.js
import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane, faCircleNotch } from '@fortawesome/free-solid-svg-icons';
import UserAvatar from '../../../UserAvatar';
import { createComment, getComments } from '../../../../services/postService';

/**
 * Simple, reliable mobile chat that actually works
 */
const ChatView = ({ event, user, comments: externalComments, onCommentsUpdate, canEdit = true }) => {
  const [comments, setComments] = useState(externalComments || []);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingComments, setLoadingComments] = useState(true);
  
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const inputRef = useRef(null);
  
  // Set external comments if provided by parent
  useEffect(() => {
    if (externalComments && externalComments.length > 0) {
      setComments(externalComments);
      setLoadingComments(false);
    }
  }, [externalComments]);

  // Simple scroll to bottom function
  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ 
        behavior: 'smooth',
        block: 'nearest'
      });
    }
  };

  // Load comments when component mounts
  useEffect(() => {
    const fetchComments = async () => {
      if (!event || !event.id || (externalComments && externalComments.length > 0)) return;
      
      setLoadingComments(true);
      try {
        const params = {
          content_type: 'event',
          content_id: event.id
        };
        
        const response = await getComments(params);
        setComments(response || []);
      } catch (error) {
      } finally {
        setLoadingComments(false);
      }
    };
    
    fetchComments();
  }, [event, externalComments]);
  
  // Scroll to bottom when comments change
  useEffect(() => {
    if (comments.length > 0) {
      // Small delay to let DOM update
      setTimeout(scrollToBottom, 100);
    }
  }, [comments]);
  
  // Handle input focus - scroll to bottom and hide navbar when keyboard appears
  const handleInputFocus = () => {
    // Add class to body to hide navbar when keyboard is open
    document.body.classList.add('chat-keyboard-open');

    // Small delay to let keyboard appear
    setTimeout(() => {
      scrollToBottom();
    }, 300);
  };

  // Handle input blur - show navbar again when keyboard closes
  const handleInputBlur = () => {
    // Remove class to show navbar again
    document.body.classList.remove('chat-keyboard-open');
  };
  
  // Format timestamp
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    
    const date = new Date(timestamp);
    const now = new Date();
    
    if (date.toDateString() === now.toDateString()) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    if (date.toDateString() === yesterday.toDateString()) {
      return 'Včeraj ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    
    return date.toLocaleDateString([], { day: 'numeric', month: 'short' }) + 
           ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };
  
  // Handle sending a new comment
  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!message.trim() || !event || !user) return;
    
    setLoading(true);
    
    try {
      const commentData = {
        content: message.trim(),
        content_type: 'event',
        content_id: event.id,
        community_id: event.community_id 
      };
      
      // Create optimistic comment
      const optimisticComment = {
        id: 'temp-' + Date.now(),
        content: message.trim(),
        content_type: 'event',
        content_id: event.id,
        user_id: user.id,
        email: user.email,
        display_name: user.displayName || user.display_name || user.email?.split('@')[0],
        profile_image: user.profileImage,
        created_at: new Date().toISOString(),
        community_id: event.community_id,
        isOptimistic: true
      };
      
      // Update local state immediately
      const updatedComments = [...comments, optimisticComment];
      setComments(updatedComments);
      
      if (onCommentsUpdate) {
        onCommentsUpdate(updatedComments);
      }
      
      // Clear input field
      setMessage('');
      
      // Send to server
      const response = await createComment(commentData);
      
      // Replace optimistic comment with server response
      const finalComments = updatedComments.map(comment =>
        comment.id === optimisticComment.id ? {
          ...response.comment,
          email: user.email,
          display_name: user.displayName || user.display_name || user.email?.split('@')[0],
          profile_image: user.profileImage
        } : comment
      );
      
      setComments(finalComments);
      
      if (onCommentsUpdate) {
        onCommentsUpdate(finalComments);
      }
      
    } catch (error) {
      
      // Remove optimistic comment on error
      const fallbackComments = comments.filter(comment => !comment.isOptimistic);
      setComments(fallbackComments);
      
      if (onCommentsUpdate) {
        onCommentsUpdate(fallbackComments);
      }
      
      alert('Prišlo je do napake pri objavi komentarja.');
    } finally {
      setLoading(false);
    }
  };
  
  // Check if a message is from current user
  const isCurrentUserMessage = (comment) => {
    return String(comment.user_id) === String(user?.id);
  };
  
  // Loading state
  if (loadingComments && comments.length === 0) {
    return (
      <div className="chat-view-loading">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Nalaganje...</span>
        </div>
        <p className="mt-3 text-light">Nalaganje komentarjev...</p>
      </div>
    );
  }

  return (
    <div className="simple-chat-view" ref={chatContainerRef}>
      {/* Messages area */}
      <div className="simple-chat-messages">
        {comments.length === 0 ? (
          <div className="empty-chat">
            <p>Še ni komentarjev. Bodi prvi!</p>
          </div>
        ) : (
          comments.map((comment, index) => {
            const isCurrentUser = isCurrentUserMessage(comment);
            const displayName = comment.display_name || comment.email?.split('@')[0] || 'Uporabnik';
            
            return (
              <div 
                key={`comment-${comment.id || index}`}
                className={`simple-chat-message ${isCurrentUser ? 'current-user' : ''} ${comment.isOptimistic ? 'optimistic' : ''}`}
              >
                {!isCurrentUser && (
                  <div className="simple-message-avatar">
                    <UserAvatar
                      profileImage={comment.profile_image}
                      displayName={comment.display_name}
                      email={comment.email}
                      size={32}
                      avatarOnly={true}
                    />
                  </div>
                )}
                
                <div className="simple-message-content">
                  {!isCurrentUser && (
                    <div className="simple-message-sender">{displayName}</div>
                  )}
                  
                  <div className="simple-message-bubble">
                    <div className="simple-message-text">{comment.content}</div>
                    <div className="simple-message-time">
                      {formatTimestamp(comment.created_at)}
                      {comment.isOptimistic && (
                        <span className="message-sending-status">
                          <FontAwesomeIcon icon={faCircleNotch} spin className="ms-1" />
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>
      
      {/* Input area - fixed at bottom */}
      <div className="simple-chat-input-area">
        <form onSubmit={handleSendMessage} className="simple-chat-form">
          <input
            ref={inputRef}
            type="text"
            className="simple-chat-input"
            placeholder="Napiši sporočilo..."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            disabled={loading || !canEdit}
          />
          <button 
            type="submit" 
            className="simple-chat-send-button" 
            disabled={!message.trim() || loading || !canEdit}
          >
            <FontAwesomeIcon icon={faPaperPlane} />
          </button>
        </form>
      </div>
    </div>
  );
};

export default ChatView;